import type { Forest, Selection } from '@/shared/lib/forest'
import type { KeyboardEvent, ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Label } from '@/shared/components/ui/label'
import { toggle, visibleRows } from '@/shared/lib/forest'
import { cn } from '@/shared/lib/utils'

export type ForestPickerProps<T> = {
	forest: Forest<T>
	selection: Selection
	onSelectionChange: (next: Selection) => void
	renderLabel?: (id: string, data: T) => ReactNode
	disabled?: boolean
	defaultExpanded?: 'roots' | 'none' | Iterable<string>
	rowHeight?: number
	maxHeight?: number
	className?: string
	testIdPrefix?: string
}

const INDENT_PX = 20

const initialExpanded = <T,>(
	forest: Forest<T>,
	defaultExpanded: ForestPickerProps<T>['defaultExpanded']
) => {
	if (defaultExpanded === 'roots') return new Set(forest.roots)
	if (!defaultExpanded || defaultExpanded === 'none') return new Set<string>()

	return new Set(defaultExpanded)
}

const defaultRenderLabel = (_id: string, data: unknown) =>
	String((data as { name?: unknown })?.name ?? '')

/**
 * One flag per indent column of `id`, top-down: is the node at that level the
 * last of its siblings? A column whose node is last draws no line below the
 * row, which is what turns plain indentation into a tree (same shape as the
 * legacy `cmp-tree` CSS). Computed per rendered row — only the virtualized
 * window pays for it.
 */
const lastAtEachLevel = (forest: Forest<unknown>, id: string) => {
	const path = [...forest.ancestors(id), id]

	return path.slice(1).map((node, index) => {
		const siblings = forest.childrenOf.get(path[index]) ?? []

		return siblings[siblings.length - 1] === node
	})
}

/** Indent column: vertical guide for the ancestors, an elbow for the row itself. */
const RowGuide = ({ isLast, isSelf }: { isLast: boolean; isSelf: boolean }) => (
	<span className='relative shrink-0 self-stretch' style={{ width: INDENT_PX }}>
		{(!isLast || isSelf) && (
			<span
				className={cn(
					'absolute top-0 left-1/2 w-px bg-border',
					isLast ? 'h-1/2' : 'bottom-0'
				)}
			/>
		)}
		{isSelf && (
			<span className='absolute top-1/2 right-0 left-1/2 h-px bg-border' />
		)}
	</span>
)

/**
 * Controlled, virtualized tree of checkboxes over any `Forest`. Selection state
 * lives in the caller; every row reads it in O(depth).
 */
export const ForestPicker = <T,>({
	forest,
	selection,
	onSelectionChange,
	renderLabel = defaultRenderLabel,
	disabled = false,
	defaultExpanded = 'none',
	rowHeight = 28,
	maxHeight = 480,
	className,
	testIdPrefix = 'forest-picker',
}: ForestPickerProps<T>) => {
	const { t } = useTranslation()
	const containerRef = useRef<HTMLDivElement>(null)
	const [expanded, setExpanded] = useState<Set<string>>(() =>
		initialExpanded(forest, defaultExpanded)
	)
	const [activeIndex, setActiveIndex] = useState(0)
	const pendingFocusRef = useRef(false)

	const rows = useMemo(() => visibleRows(forest, expanded), [forest, expanded])
	const indexOfId = useMemo(
		() => new Map(rows.map((r, i) => [r.id, i])),
		[rows]
	)

	// Roving tabindex: collapsing a node (or a forest with zero visible rows)
	// can leave the previously active index below/at/above range, which would
	// leave no row focusable. Clamp it back in range during render (adjusting
	// state from props, not an effect) so it never renders out-of-range for
	// even one frame. Compare against the clamped target (not a one-sided
	// inequality) so the guard is false once `activeIndex` already equals it —
	// otherwise `rows.length === 0` clamps to 0 forever without converging.
	const clampedActiveIndex = Math.max(Math.min(activeIndex, rows.length - 1), 0)
	if (clampedActiveIndex !== activeIndex) {
		setActiveIndex(clampedActiveIndex)
	}

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => containerRef.current,
		estimateSize: () => rowHeight,
		overscan: 8,
	})

	useEffect(() => {
		if (!pendingFocusRef.current) return
		pendingFocusRef.current = false
		virtualizer.scrollToIndex(activeIndex)
		// scrollToIndex schedules an async re-measure/re-render, so the target row
		// may not be mounted yet in this tick — defer the focus to the next frame.
		const frame = requestAnimationFrame(() => {
			const element = containerRef.current?.querySelector<HTMLElement>(
				`[data-index="${activeIndex}"]`
			)
			element?.focus()
		})

		return () => cancelAnimationFrame(frame)
	}, [activeIndex, rows, virtualizer])

	const moveTo = (index: number) => {
		if (index < 0 || index >= rows.length) return
		pendingFocusRef.current = true
		setActiveIndex(index)
	}
	const setExpandedFor = (id: string, open: boolean) =>
		setExpanded((previous) => {
			const next = new Set(previous)
			if (open) next.add(id)
			else next.delete(id)

			return next
		})
	const handleToggleSelection = (id: string) => {
		if (disabled) return
		onSelectionChange(toggle(forest, selection, id))
	}

	const handleKeyDown = (
		event: KeyboardEvent<HTMLDivElement>,
		index: number
	) => {
		const current = rows[index]
		if (!current) return
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				moveTo(index + 1)
				break
			case 'ArrowUp':
				event.preventDefault()
				moveTo(index - 1)
				break
			case 'ArrowRight':
				event.preventDefault()
				if (!current.hasChildren) break
				if (expanded.has(current.id)) moveTo(index + 1)
				else setExpandedFor(current.id, true)
				break
			case 'ArrowLeft': {
				event.preventDefault()
				if (current.hasChildren && expanded.has(current.id)) {
					setExpandedFor(current.id, false)
					break
				}
				const parent = forest.parentOf.get(current.id) ?? null
				if (parent !== null) moveTo(indexOfId.get(parent) ?? index)
				break
			}
			case ' ':
				event.preventDefault()
				handleToggleSelection(current.id)
				break
			default:
		}
	}

	return (
		<div
			ref={containerRef}
			role='tree'
			data-testid={`${testIdPrefix}-tree`}
			className={cn('relative scroll-fade overflow-auto', className)}
			style={{ maxHeight }}
		>
			<div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
				{virtualizer.getVirtualItems().map((virtualRow) => {
					const rowData = rows[virtualRow.index]
					const { id, depth, hasChildren } = rowData
					const isExpanded = expanded.has(id)
					const checked = selection.isChecked(id)
					const indeterminate = !checked && selection.isIndeterminate(id)

					return (
						<div
							key={id}
							role='treeitem'
							aria-level={depth + 1}
							aria-expanded={hasChildren ? isExpanded : undefined}
							aria-checked={indeterminate ? 'mixed' : checked}
							aria-disabled={disabled || undefined}
							data-testid={`${testIdPrefix}-row-${id}`}
							data-index={virtualRow.index}
							tabIndex={virtualRow.index === activeIndex ? 0 : -1}
							className='absolute top-0 left-0 flex w-full items-center gap-1 outline-none focus-visible:bg-accent/40'
							style={{
								height: rowHeight,
								transform: `translateY(${virtualRow.start}px)`,
							}}
							onFocus={() => setActiveIndex(virtualRow.index)}
							onKeyDown={(event) => handleKeyDown(event, virtualRow.index)}
						>
							{depth > 0 && (
								<span
									aria-hidden
									className='flex self-stretch'
									data-testid={`${testIdPrefix}-guides-${id}`}
								>
									{lastAtEachLevel(forest, id).map((isLast, level) => (
										<RowGuide
											key={level}
											isLast={isLast}
											isSelf={level === depth - 1}
										/>
									))}
								</span>
							)}
							{hasChildren ? (
								<button
									type='button'
									tabIndex={-1}
									aria-label={
										isExpanded
											? t('tree.collapse', { defaultValue: 'Collapse' })
											: t('tree.expand', { defaultValue: 'Expand' })
									}
									data-testid={`${testIdPrefix}-toggle-${id}`}
									className='flex size-5 cursor-pointer items-center justify-center border-none bg-transparent'
									onClick={() => setExpandedFor(id, !isExpanded)}
								>
									{isExpanded ? (
										<ChevronDownIcon className='size-4' />
									) : (
										<ChevronRightIcon className='size-4' />
									)}
								</button>
							) : (
								<span className='size-5' />
							)}
							<div className='flex items-center gap-2'>
								<Checkbox
									id={`${testIdPrefix}-checkbox-${id}`}
									tabIndex={-1}
									disabled={disabled}
									checked={checked}
									indeterminate={indeterminate}
									data-testid={`${testIdPrefix}-checkbox-${id}`}
									onChange={() => handleToggleSelection(id)}
								/>
								<Label
									htmlFor={`${testIdPrefix}-checkbox-${id}`}
									className='font-normal select-none'
								>
									{renderLabel(id, forest.dataOf.get(id) as T)}
								</Label>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
